import { useState } from "react";
import { Button } from "@panda0/panda0-ui";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useFormContext } from "react-hook-form";

interface JSONViewerProps {
  src: any;
  path?: string;
  level?: number;
}

// Recursive JSON Viewer component with collapsing functionality
const JSONViewer = ({ src, path = "", level = 0 }: JSONViewerProps) => {
  const [isExpanded, setIsExpanded] = useState(level < 1); // Auto-expand first level

  if (typeof src !== "object" || src === null) {
    // Render primitive values
    return (
      <span
        className={typeof src === "string" ? "text-green-600" : "text-blue-600"}
      >
        {JSON.stringify(src)}
      </span>
    );
  }

  const isArray = Array.isArray(src);
  const isEmpty = Object.keys(src).length === 0;

  if (isEmpty) {
    return <span>{isArray ? "[]" : "{}"}</span>;
  }

  return (
    <div className="pl-4">
      <span
        onClick={() => setIsExpanded(!isExpanded)}
        className="cursor-pointer inline-flex items-center"
      >
        {isExpanded ? (
          <ChevronDown className="h-3 w-3 mr-1 text-gray-500" />
        ) : (
          <ChevronRight className="h-3 w-3 mr-1 text-gray-500" />
        )}
        <span className="text-gray-700 select-none">
          {path ? path : isArray ? "Array" : "Object"}
          {!isExpanded && (
            <span className="text-gray-500 text-xs ml-1">
              ({Object.keys(src).length}{" "}
              {Object.keys(src).length === 1 ? "item" : "items"})
            </span>
          )}
        </span>
      </span>

      {isExpanded && (
        <div className="border-l-2 border-gray-200 pl-2">
          {Object.entries(src).map(([key, value], i) => (
            <div key={key} className="py-1">
              <span className="text-purple-600 mr-1">{isArray ? i : key}:</span>
              <JSONViewer src={value} path={key} level={level + 1} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function FormDebugPanel() {
  const { getValues, formState } = useFormContext();
  const [isOpen, setIsOpen] = useState(false);

  if (process.env.NODE_ENV === "production") {
    return null;
  }

  const formValues = getValues();
  const { errors } = formState;

  return (
    <div className="form-debug-panel fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 flex flex-col items-center">
      <Button
        className="w-fit mb-2 shadow-md"
        variant="secondary"
        onClick={() => setIsOpen(!isOpen)}
        text={isOpen ? "Hide Debug" : "Show Debug"}
      />

      {isOpen && (
        <div className="debug-content bg-white p-4 rounded-md border shadow-lg max-h-[80vh] overflow-auto w-[600px]">
          <h3 className="text-lg font-semibold mb-2">Form Values:</h3>
          <div className="bg-gray-50 p-2 rounded mb-4 font-mono text-sm">
            <JSONViewer src={formValues} />
          </div>

          <h3 className="text-lg font-semibold mb-2">Errors:</h3>
          <div className="bg-gray-50 p-2 rounded mb-4 font-mono text-sm">
            {Object.keys(errors).length > 0 ? (
              <JSONViewer src={errors} />
            ) : (
              <span className="text-gray-500">No errors</span>
            )}
          </div>

          <h3 className="text-lg font-semibold mb-2">Form State:</h3>
          <div className="bg-gray-50 p-2 rounded mb-4 font-mono text-sm">
            <div>
              isDirty:{" "}
              <span
                className={
                  formState.isDirty ? "text-green-600" : "text-red-600"
                }
              >
                {String(formState.isDirty)}
              </span>
            </div>
            <div>
              isSubmitting:{" "}
              <span
                className={
                  formState.isSubmitting ? "text-green-600" : "text-red-600"
                }
              >
                {String(formState.isSubmitting)}
              </span>
            </div>
            <div>
              isValid:{" "}
              <span
                className={
                  formState.isValid ? "text-green-600" : "text-red-600"
                }
              >
                {String(formState.isValid)}
              </span>
            </div>
            <div>submitCount: {formState.submitCount}</div>
          </div>

          <div className="flex justify-end mt-2">
            <Button
              variant="ghost"
              size="sm"
              text="Close"
              onClick={() => setIsOpen(false)}
              className="text-xs"
            />
          </div>
        </div>
      )}
    </div>
  );
}
